// Composite analytics provider: one primary + N secondary sinks.
//
// The analytics layer has a single provider slot (see ./index.ts). To send
// the SAME typed events to more than one vendor we wrap them here. WRITES
// (trackEvent, identifyUser) fan out to the primary and every sink, each
// isolated in its own try/catch so one failing vendor never starves another.
// READS (checkGate, getConfig, getExperiment) come from the PRIMARY only:
// feature-flag / experiment ownership stays with Statsig; sinks such as
// Customer.io are not flag vendors.

import { logger } from '@/lib/telemetry/logger';
import type { AnalyticsConfig, AnalyticsProvider, ExperimentResult } from './types';

export class CompositeAnalyticsProvider implements AnalyticsProvider {
  private readonly primary: AnalyticsProvider;
  private readonly sinks: readonly AnalyticsProvider[];

  constructor(primary: AnalyticsProvider, sinks: readonly AnalyticsProvider[]) {
    this.primary = primary;
    this.sinks = sinks;
  }

  /**
   * Resolves when the PRIMARY is ready. The readiness signal that gates
   * flag-dependent rendering (analytics-provider.tsx -> markProviderReady)
   * must track the flag vendor, not a slow attribution sink, so sinks
   * initialize fire-and-forget while the primary init is awaited.
   */
  async initialize(config: AnalyticsConfig): Promise<void> {
    for (const sink of this.sinks) {
      void sink.initialize(config).catch((err: unknown) => {
        logger.warn('Analytics sink initialize failed (fire-and-forget)', {
          error: err instanceof Error ? err.message.slice(0, 200) : String(err),
        });
      });
    }
    await this.primary.initialize(config);
  }

  checkGate(name: string): boolean {
    return this.primary.checkGate(name);
  }

  getConfig(name: string): Record<string, unknown> {
    return this.primary.getConfig(name);
  }

  getExperiment(name: string): ExperimentResult {
    return this.primary.getExperiment(name);
  }

  trackEvent(name: string, properties: Record<string, unknown>): void {
    this.fanOut('trackEvent', (p) => {
      p.trackEvent(name, properties);
    });
  }

  identifyUser(userId: string, attributes: Record<string, unknown>): void {
    this.fanOut('identifyUser', (p) => {
      p.identifyUser(userId, attributes);
    });
  }

  shutdown(): void {
    this.fanOut('shutdown', (p) => {
      p.shutdown();
    });
  }

  private fanOut(op: string, call: (p: AnalyticsProvider) => void): void {
    for (const p of [this.primary, ...this.sinks]) {
      try {
        call(p);
      } catch (err: unknown) {
        logger.warn('Analytics fan-out call failed (fire-and-forget)', {
          op,
          error: err instanceof Error ? err.message.slice(0, 200) : String(err),
        });
      }
    }
  }
}

export function createCompositeProvider(
  primary: AnalyticsProvider,
  sinks: readonly AnalyticsProvider[],
): AnalyticsProvider {
  return new CompositeAnalyticsProvider(primary, sinks);
}
